/**
 * ============================================================
 * KEJJA OS — src/pages/meals/RecipesPage.tsx  (v1.1)
 * Recipe library + editor with step-by-step cooking.
 * ============================================================
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import {
  Plus, Heart, Clock, Users, Trash2, Pencil, X,
  ChefHat, GripVertical, Timer, ChevronUp, ChevronDown, ListOrdered,
} from 'lucide-react';

import { useMealsContext } from './MealsLayout';
import {
  useRecipes, useRecipe, useCreateRecipe, useUpdateRecipe, useDeleteRecipe, useSeedRecipes,
} from '@/hooks/useMeals';
import type { Recipe, RecipeIngredient, RecipeStep } from '@/lib/mealsApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import ImportRecipeModal from './ImportRecipeModal';
import { Sparkles } from 'lucide-react';

// ============================================================
// Types for the form
// ============================================================

interface IngredientDraft {
  name: string;
  quantity: string;
  unit: string;
  note: string;
}

interface StepDraft {
  title: string;
  body: string;
  timerMinutes: string;   // editable as minutes; we convert on save
}

interface FormState {
  id: string | null;
  name: string;
  description: string;
  servings: number;
  prepMinutes: string;
  cookMinutes: string;
  tags: string;
  icon: string;
  isFavorite: boolean;
  ingredients: IngredientDraft[];
  steps: StepDraft[];
  tips: string;            // stored in `method` column
}

const EMPTY_FORM: FormState = {
  id: null,
  name: '',
  description: '',
  servings: 4,
  prepMinutes: '',
  cookMinutes: '',
  tags: '',
  icon: '',
  isFavorite: false,
  ingredients: [{ name: '', quantity: '', unit: '', note: '' }],
  steps: [{ title: '', body: '', timerMinutes: '' }],
  tips: '',
};

// ============================================================
// Helpers
// ============================================================

function minutesToSeconds(m: string): number | undefined {
  if (!m.trim()) return undefined;
  const n = parseFloat(m);
  if (Number.isNaN(n) || n <= 0) return undefined;
  return Math.round(n * 60);
}

function secondsToMinutes(s: number | undefined): string {
  if (!s || s <= 0) return '';
  return String(Math.round(s / 60));
}

function formatTimer(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

// ============================================================
// Main page
// ============================================================

export default function RecipesPage() {
  const { homeId } = useMealsContext();
  const { data: listData, isLoading } = useRecipes(homeId);
  const seed = useSeedRecipes(homeId);
  const create = useCreateRecipe(homeId);
  const update = useUpdateRecipe(homeId);
  const remove = useDeleteRecipe(homeId);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [err, setErr] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  const recipes = listData?.recipes ?? [];

  const openNew = () => { setForm(EMPTY_FORM); setShowForm(true); setErr(null); setViewingId(null); };

  const openEdit = (r: Recipe, ingredients?: RecipeIngredient[]) => {
    setForm({
      id: r.id,
      name: r.name,
      description: r.description ?? '',
      servings: r.servings,
      prepMinutes: r.prepMinutes?.toString() ?? '',
      cookMinutes: r.cookMinutes?.toString() ?? '',
      tags: (r.tags ?? []).join(', '),
      icon: r.icon ?? '',
      isFavorite: r.isFavorite,
      ingredients: (ingredients ?? []).map((ing) => ({
        name: ing.name,
        quantity: ing.quantity ?? '',
        unit: ing.unit ?? '',
        note: ing.note ?? '',
      })).concat([{ name: '', quantity: '', unit: '', note: '' }]),
      steps: (r.steps ?? []).map((s: RecipeStep) => ({
        title: s.title ?? '',
        body: s.body ?? '',
        timerMinutes: secondsToMinutes(s.timerSeconds),
      })).concat([{ title: '', body: '', timerMinutes: '' }]),
      tips: r.method ?? '',
    });
    setShowForm(true);
    setErr(null);
    setViewingId(null);
  };

  const buildBody = () => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    servings: form.servings,
    prepMinutes: form.prepMinutes ? parseInt(form.prepMinutes, 10) : null,
    cookMinutes: form.cookMinutes ? parseInt(form.cookMinutes, 10) : null,
    tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    icon: form.icon || null,
    isFavorite: form.isFavorite,
    method: form.tips.trim() || null,
    ingredients: form.ingredients
      .filter((ing) => ing.name.trim())
      .map((ing) => ({
        name: ing.name.trim(),
        quantity: ing.quantity.trim() || null,
        unit: ing.unit.trim() || null,
        note: ing.note.trim() || null,
      })),
    steps: form.steps
      .filter((s) => s.title.trim() || s.body.trim())
      .map((s) => {
        const out: RecipeStep = {
          title: s.title.trim(),
          body: s.body.trim(),
        };
        const t = minutesToSeconds(s.timerMinutes);
        if (t !== undefined) out.timerSeconds = t;
        return out;
      }),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!form.name.trim()) { setErr('Recipe name is required.'); return; }

    const body = buildBody();
    const onDone = () => { setShowForm(false); setForm(EMPTY_FORM); };
    const onError = (e: Error) => setErr(e.message ?? 'Could not save.');

    if (form.id) {
      update.mutate({ recipeId: form.id, patch: body }, { onSuccess: onDone, onError });
    } else {
      create.mutate(body, { onSuccess: onDone, onError });
    }
  };

  const handleDelete = (r: Recipe) => {
    if (confirm(`Delete "${r.name}"? This can't be undone.`)) remove.mutate(r.id);
  };

  const handleSeed = (replace: boolean) => {
    if (replace) {
      const ok = confirm(
        `This will REPLACE all ${recipes.length} of your recipes with 20 Kenyan classics (Ugali, Chapati, Pilau, Nyama Choma, Mahamri…).\n\nYour existing recipes will be soft-deleted. Continue?`,
      );
      if (!ok) return;
    }
    seed.mutate(replace);
  };

  // Ingredient row helpers
  const addIngredient = () => setForm({ ...form, ingredients: [...form.ingredients, { name: '', quantity: '', unit: '', note: '' }] });
  const updateIngredient = (i: number, patch: Partial<IngredientDraft>) => {
    const next = [...form.ingredients];
    next[i] = { ...next[i]!, ...patch };
    setForm({ ...form, ingredients: next });
  };
  const removeIngredient = (i: number) => {
    const next = form.ingredients.filter((_, idx) => idx !== i);
    setForm({ ...form, ingredients: next.length > 0 ? next : [{ name: '', quantity: '', unit: '', note: '' }] });
  };

  // Step row helpers
  const addStep = () => setForm({ ...form, steps: [...form.steps, { title: '', body: '', timerMinutes: '' }] });
  const updateStep = (i: number, patch: Partial<StepDraft>) => {
    const next = [...form.steps];
    next[i] = { ...next[i]!, ...patch };
    setForm({ ...form, steps: next });
  };
  const removeStep = (i: number) => {
    const next = form.steps.filter((_, idx) => idx !== i);
    setForm({ ...form, steps: next.length > 0 ? next : [{ title: '', body: '', timerMinutes: '' }] });
  };
  const moveStep = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= form.steps.length) return;
    const next = [...form.steps];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setForm({ ...form, steps: next });
  };

  // ============================================================
  // Loading
  // ============================================================
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-16 rounded-xl" />
      </div>
    );
  }

  // ============================================================
  // Empty state
  // ============================================================
  if (recipes.length === 0 && !showForm) {
    return (
      <Card className="border-dashed border-amber-500/30 bg-gradient-to-br from-amber-950/20 to-orange-950/10">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 mx-auto flex items-center justify-center mb-4">
            <ChefHat className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-lg text-foreground">No recipes yet</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Start with 20 Kenyan classics — step-by-step methods, ingredients, and cooking tips — or build your own family library.
          </p>
          <div className="flex gap-2 justify-center mt-5 flex-wrap">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleSeed(false)} disabled={seed.isPending}>
              {seed.isPending ? 'Setting up…' : 'Add 20 Kenyan classics'}
            </Button>
            <Button variant="outline" onClick={openNew}>Start blank</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ============================================================
  // Main view
  // ============================================================
  return (
    <div className="space-y-5 pb-12">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-bold">Recipe library</h1>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {recipes.length} recipe{recipes.length === 1 ? '' : 's'} · {recipes.filter((r) => r.isFavorite).length} favorites
          </p>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          {recipes.length > 0 && !showForm && (
            <Button
              size="sm" variant="outline"
              onClick={() => handleSeed(true)}
              disabled={seed.isPending}
              className="text-xs"
            >
              {seed.isPending ? 'Replacing…' : 'Reset to 20 classics'}
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            className="text-xs"
            onClick={() => setShowImport(true)}
          >
            <Sparkles className="w-3 h-3 mr-1 text-amber-600" /> Import
          </Button>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={showForm ? () => setShowForm(false) : openNew}
          >
            {showForm ? <><X className="w-4 h-4 mr-1" /> Close</> : <><Plus className="w-4 h-4 mr-1" /> New</>}
          </Button>
        </div>
      </div>

      {/* ============ FORM ============ */}
      {showForm && (
        <Card className="border-amber-300 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-950/10">
          <CardContent className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basics */}
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <div>
                  <Label htmlFor="r-name" className="text-xs">Name</Label>
                  <Input
                    id="r-name" autoFocus
                    value={form.name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Chapati"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Fav</Label>
                  <Button
                    type="button" variant={form.isFavorite ? 'default' : 'outline'}
                    className={cn('mt-1 h-9 w-9 p-0', form.isFavorite && 'bg-rose-600 hover:bg-rose-700 text-white')}
                    onClick={() => setForm({ ...form, isFavorite: !form.isFavorite })}
                  >
                    <Heart className={cn('w-4 h-4', form.isFavorite && 'fill-current')} />
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="r-desc" className="text-xs">Short description</Label>
                <Input
                  id="r-desc"
                  value={form.description}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, description: e.target.value })}
                  placeholder="One-line tagline"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label htmlFor="r-ico" className="text-xs">Icon</Label>
                  <Input
                    id="r-ico" maxLength={2}
                    value={form.icon}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, icon: e.target.value })}
                    placeholder="🍛" className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="r-serve" className="text-xs">Servings</Label>
                  <Input
                    id="r-serve" type="number" min="1" max="100"
                    value={form.servings}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, servings: Number(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="r-prep" className="text-xs">Prep (min)</Label>
                  <Input
                    id="r-prep" type="number" min="0"
                    value={form.prepMinutes}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, prepMinutes: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="r-cook" className="text-xs">Cook (min)</Label>
                  <Input
                    id="r-cook" type="number" min="0"
                    value={form.cookMinutes}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, cookMinutes: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="r-tags" className="text-xs">Tags</Label>
                <Input
                  id="r-tags"
                  value={form.tags}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, tags: e.target.value })}
                  placeholder="Kenyan, quick, vegetarian (comma-separated)"
                  className="mt-1"
                />
              </div>

              {/* ============ INGREDIENTS ============ */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Ingredients</Label>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={addIngredient}>
                    <Plus className="w-3 h-3 mr-0.5" /> row
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {form.ingredients.map((ing, i) => (
                    <div key={i} className="flex gap-1.5 items-center">
                      <GripVertical className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />
                      <Input
                        value={ing.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateIngredient(i, { name: e.target.value })}
                        placeholder="Ingredient"
                        className="flex-1 h-8 text-xs"
                      />
                      <Input
                        value={ing.quantity}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateIngredient(i, { quantity: e.target.value })}
                        placeholder="qty"
                        className="w-16 h-8 text-xs"
                      />
                      <Input
                        value={ing.unit}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateIngredient(i, { unit: e.target.value })}
                        placeholder="unit"
                        className="w-16 h-8 text-xs"
                      />
                      <Button
                        type="button" size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600"
                        onClick={() => removeIngredient(i)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* ============ STEPS ============ */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                    <ListOrdered className="w-3.5 h-3.5" /> Cooking steps
                  </Label>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={addStep}>
                    <Plus className="w-3 h-3 mr-0.5" /> step
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">
                  Add as many steps as you like. Each step can have an optional timer (in minutes).
                </p>
                <div className="space-y-2">
                  {form.steps.map((step, i) => (
                    <div key={i} className="rounded-lg border border-border bg-background/50 p-2.5 space-y-2">
                      <div className="flex gap-1.5 items-center">
                        <div className="h-6 w-6 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </div>
                        <Input
                          value={step.title}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => updateStep(i, { title: e.target.value })}
                          placeholder="Step title (e.g. Boil the water)"
                          className="flex-1 h-8 text-xs font-medium"
                        />
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <Button
                            type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground"
                            onClick={() => moveStep(i, -1)} disabled={i === 0}
                            aria-label="Move up"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button
                            type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground"
                            onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1}
                            aria-label="Move down"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                          <Button
                            type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600"
                            onClick={() => removeStep(i)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <textarea
                        value={step.body}
                        onChange={(e) => updateStep(i, { body: e.target.value })}
                        placeholder="Describe what to do in this step…"
                        className="w-full min-h-[60px] rounded-md border border-border bg-background p-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                      />
                      <div className="flex items-center gap-1.5">
                        <Timer className="w-3 h-3 text-muted-foreground" />
                        <Input
                          type="number" min="0" step="0.5"
                          value={step.timerMinutes}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => updateStep(i, { timerMinutes: e.target.value })}
                          placeholder="Optional timer"
                          className="h-7 text-xs flex-1"
                        />
                        <span className="text-[10px] text-muted-foreground">min</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ============ TIPS ============ */}
              <div className="border-t border-border pt-4">
                <Label htmlFor="r-tips" className="text-xs font-semibold uppercase tracking-wider">
                  Tips & variations <span className="font-normal text-muted-foreground normal-case">(optional)</span>
                </Label>
                <textarea
                  id="r-tips"
                  value={form.tips}
                  onChange={(e) => setForm({ ...form, tips: e.target.value })}
                  placeholder="e.g. 'Too dry? Add 2 tbsp warm water.' or variations for different regions…"
                  className="w-full mt-1 min-h-[80px] rounded-md border border-border bg-background p-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                />
              </div>

              {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

              <div className="flex gap-2 pt-1">
                <Button
                  type="submit" className="bg-amber-600 hover:bg-amber-700 text-white flex-1"
                  disabled={create.isPending || update.isPending}
                >
                  {create.isPending || update.isPending ? 'Saving…' : form.id ? 'Save changes' : 'Add recipe'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* ============ GRID ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {recipes.map((r) => (
          <RecipeCard
            key={r.id} recipe={r}
            isExpanded={viewingId === r.id}
            onToggle={() => setViewingId(viewingId === r.id ? null : r.id)}
            onEdit={(ingredients) => openEdit(r, ingredients)}
            onDelete={() => handleDelete(r)}
          />
        ))}
      </div>
      {showImport && <ImportRecipeModal homeId={homeId!} onClose={() => setShowImport(false)} />}
    </div>
  );
}

// ============================================================
// RecipeCard — expanded view shows steps + tips
// ============================================================

function RecipeCard({
  recipe, isExpanded, onToggle, onEdit, onDelete,
}: {
  recipe: Recipe;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (ingredients?: RecipeIngredient[]) => void;
  onDelete: () => void;
}) {
  const { homeId } = useMealsContext();
  const { data: detail } = useRecipe(homeId, isExpanded ? recipe.id : undefined);
  const ingredients = detail?.recipe?.ingredients ?? [];
  const steps = (detail?.recipe?.steps ?? recipe.steps ?? []) as RecipeStep[];

  return (
    <Card
      className={cn(
        'overflow-hidden transition-colors',
        !isExpanded && 'cursor-pointer',
        isExpanded && 'ring-1 ring-amber-500/40',
      )}
      onClick={!isExpanded ? onToggle : undefined}
    >
      <CardContent className="p-3">
        {/* ===== HEADER ===== */}
        <div className="flex items-start gap-3 cursor-pointer" onClick={isExpanded ? onToggle : undefined}>
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
            style={{
              backgroundColor: recipe.color ? `${recipe.color}20` : 'var(--muted)',
              color: recipe.color ?? 'var(--muted-foreground)',
            }}
          >
            {recipe.icon || recipe.name[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">{recipe.name}</span>
              {recipe.isFavorite && <Heart className="w-3 h-3 fill-rose-500 text-rose-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
              <span className="flex items-center gap-0.5">
                <Users className="w-3 h-3" /> {recipe.servings}
              </span>
              {(recipe.prepMinutes !== null || recipe.cookMinutes !== null) && (
                <span className="flex items-center gap-0.5">
                  <Clock className="w-3 h-3" />
                  {(recipe.prepMinutes ?? 0) + (recipe.cookMinutes ?? 0)}m
                </span>
              )}
              {recipe.steps && recipe.steps.length > 0 && (
                <span className="flex items-center gap-0.5">
                  <ListOrdered className="w-3 h-3" /> {recipe.steps.length} steps
                </span>
              )}
              {(recipe.tags ?? []).slice(0, 2).map((t) => (
                <Badge key={t} variant="outline" className="text-[9px] h-4 px-1 font-normal">{t}</Badge>
              ))}
            </div>
            {recipe.description && (
              <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{recipe.description}</div>
            )}
          </div>
        </div>

        {/* ===== EXPANDED DETAIL ===== */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border space-y-4" onClick={(e) => e.stopPropagation()}>
            {/* Ingredients */}
            {ingredients.length > 0 ? (
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Ingredients
                </h3>
                <ul className="text-xs space-y-0.5">
                  {ingredients.map((ing) => (
                    <li key={ing.id} className="text-foreground">
                      <span className="tabular-nums text-muted-foreground">
                        {ing.quantity}{ing.unit && ` ${ing.unit}`}
                      </span>{' '}
                      <span>{ing.name}</span>
                      {ing.note && <span className="text-muted-foreground"> · {ing.note}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            ) : (
              <div className="text-[11px] text-muted-foreground">No ingredients listed.</div>
            )}

            {/* Steps */}
            {steps.length > 0 && (
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <ListOrdered className="w-3.5 h-3.5" /> Method
                </h3>
                <ol className="space-y-3">
                  {steps.map((s, i) => (
                    <li key={i} className="flex gap-2.5">
                      <div className="h-6 w-6 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        {s.title && (
                          <div className="text-xs font-semibold text-foreground">{s.title}</div>
                        )}
                        {s.body && (
                          <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed whitespace-pre-wrap">
                            {s.body}
                          </div>
                        )}
                        {s.timerSeconds && s.timerSeconds > 0 && (
                          <Badge variant="outline" className="mt-1 text-[9px] h-4 px-1.5 font-normal bg-amber-50 dark:bg-amber-950/20 border-amber-300 text-amber-700 dark:text-amber-300">
                            <Timer className="w-2.5 h-2.5 mr-0.5" /> {formatTimer(s.timerSeconds)}
                          </Badge>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Tips */}
            {recipe.method && (
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Tips &amp; variations
                </h3>
                <div className="text-xs text-foreground whitespace-pre-wrap leading-relaxed bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200 dark:border-amber-900/40 rounded-lg p-2.5">
                  {recipe.method}
                </div>
              </section>
            )}

            {/* Actions */}
            <div className="flex gap-1 pt-2 border-t border-border">
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => onEdit(ingredients)}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground hover:text-rose-600" onClick={onDelete}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
